import { useChat } from '@ai-sdk/react';
import {
  type ArtifactEventPayload,
  type ChatStepSlug,
  type DraftArtifactKind,
  type StudyStepSlug,
} from '@sigora/domain';
import { generateId } from 'ai';
import {
  type Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { type BriefReadView, fetchMessages } from '../api/client.ts';
import { isApiError, runningStepOf } from '../api/errors.ts';
import { KIND_LABELS } from '../artifacts/state.ts';
import { ChatPanel, type ChatPanelHandle, type DraftStore } from '../chat/ChatPanel.tsx';
import { BUSY_ELSEWHERE_NOTICE, CHAT_GREETING } from '../chat/chat-copy.ts';
import { collectArtifactParts } from '../chat/collect.ts';
import type { SigoraUIMessage } from '../chat/events.ts';
import { createHttpTurnStreamSource, createTurnChatTransport } from '../chat/transport.ts';
import {
  type FollowUpMemory,
  type FollowUpScope,
  FollowUpThread,
} from './follow-ups/FollowUpThread.tsx';

/**
 * What the conversation says in front of a reply this view found already being written — a page
 * opened, refreshed or returned to mid-reply — when the reply would otherwise continue the agent's
 * last message. It is also what makes the reply a message of its own: the chat state machine
 * continues the last message when that message is the agent's. Local to this view, like the
 * greeting and the approval line. The wording is placeholder copy pending product review.
 */
const ALREADY_REPLYING_LINE = 'Sigora was already replying.';

/**
 * One copy of each message, and an introducing line goes with the copy it introduced.
 *
 * Both halves of the conversation are rendered before they are stored — a message the moment it is
 * typed, a reply as it streams — and both are later renamed to the row they became, so a list can
 * legitimately hold two copies of one id. The first is kept: it is either where the transcript put
 * it or where the user watched it appear, and the later one is the same message arriving by the
 * other route.
 *
 * The second half is the part that is easy to leave out. A view-local `system` line exists to
 * introduce the reply that follows it — "Sigora was already replying.", "You approved the research
 * questions." — so when that reply turns out to be a copy of something already further up,
 * the line has nothing left to introduce and reads as a sentence printed below the thing it
 * announces. Nothing else can be a `system` message here: the transcript read never serves the
 * role, so every one on screen was put there by this view for exactly that purpose.
 *
 * Adjacency rather than position, deliberately. "The line is last" is true for a moment on every
 * attach — between the line going in and the first chunk of the reply arriving — so a rule keyed on
 * it would take the line down mid-stream if a transcript read happened to land in that window.
 */
const collapseToOneCopy = (messages: readonly SigoraUIMessage[]): SigoraUIMessage[] => {
  const seen = new Set<string>();
  const kept: SigoraUIMessage[] = [];

  for (const message of messages) {
    if (seen.has(message.id)) {
      if (kept.at(-1)?.role === 'system') {
        kept.pop();
      }

      continue;
    }

    seen.add(message.id);
    kept.push(message);
  }

  return kept;
};

/**
 * How long to wait between reads of this thread while a turn runs on another step, so the notice
 * and the Send it holds go when the turn does and a turn that turns out to be this thread's is
 * attached to.
 */
const BUSY_RECHECK_MS = 3_000;

/**
 * How long a cancellation this view asked for can still explain a refused send.
 *
 * The worker lets go of a stopped turn in about two seconds on a local stack, so this is generous
 * by a wide margin — it is not a timeout, it is the point past which a turn refusing a send cannot
 * plausibly be the one that was stopped, and something started elsewhere is the better explanation.
 */
const CANCELLATION_EXPLAINS_REFUSAL_FOR_MS = 10_000;

/**
 * How often the chat publishes its messages while a reply streams.
 *
 * A turn's stream opened part way through replays every frame so far in one burst — hundreds, for
 * a model's reply — and each frame is a write to the chat. Published one by one, every write is a
 * render of the thread, and React counts a burst of them as nested updates; past fifty it refuses
 * the next as an infinite loop. Held to one publication per interval, a burst is a handful of
 * renders and a live reply still reads as typing.
 */
const STREAM_PUBLISH_MS = 50;

/**
 * What each artifact part says, or null when the parts say what `last` said.
 *
 * The chat copies a message every time it writes to it, so a part keeps its identity only across
 * a publication that did not touch its message. A part handed back unchanged reuses the last
 * reading of it; any other is read again.
 */
const sayChanged = (
  parts: readonly ArtifactEventPayload[],
  last: { parts: readonly ArtifactEventPayload[]; said: readonly string[] } | null,
): string[] | null => {
  const said = parts.map((part, index) =>
    last !== null && last.parts[index] === part
      ? (last.said[index] ?? JSON.stringify(part))
      : JSON.stringify(part),
  );
  const unchanged =
    last !== null &&
    said.length === last.said.length &&
    said.every((text, index) => text === last.said[index]);

  return unchanged ? null : said;
};

/**
 * Text handed back to the composer, and the one route by which anything does. Held by the shell,
 * per step, because this thread is unmounted as the researcher walks and an offer made to it — a
 * refused send answering after they left — has to wait for the step to be drawn again.
 */
export interface ComposerOffer {
  text: string;
  token: number;
  /** Bring the composer to the reader even when it declines the words; see `ChatPanel`. */
  reveal?: boolean;
  /**
   * The card the next question is about, from the Brief's Ask about this. It holds whether or not
   * the composer takes the words, since the researcher asked about that card either way.
   */
  scope?: FollowUpScope;
  /**
   * Whether words in the composer take the offer after them rather than declining it; see
   * `ChatPanel`.
   */
  append?: true;
  onTaken?: () => void;
  onAnswered?: () => void;
}

/** What the shell asks of the thread on screen. */
export interface StepThreadHandle {
  /**
   * An approval the server answered with a turn: the agent is already drafting what comes next,
   * written into this thread, which is the step whose card was approved.
   */
  adoptApproval: (approved: { kind: DraftArtifactKind; turnId: string }) => void;
  /**
   * Words for Sigora from a step's own control, with the drawer opened by the shell.
   *
   * Without `send` they are offered to the composer, which declines them over words of its own;
   * blank, they only bring the composer to the reader. With `send` they are sent as the
   * researcher's message when this thread can send now, decided once its first transcript read has
   * landed, and otherwise go into the composer, after any words already there. The latest request
   * replaces one not yet decided, and one this thread has not decided goes when it unmounts or the
   * step on screen changes under it.
   */
  ask: (text: string, options: { send: boolean }) => void;
}

export interface StepThreadProps {
  ref?: Ref<StepThreadHandle>;
  studyId: string;
  /** The step whose thread this is. The component is keyed by it, so it never changes. */
  step: ChatStepSlug;
  /**
   * The step on screen. Two steps can show one thread (Fielding and Analyzing, `threadStepOf`), so the
   * thread stays mounted across a move between them; a request to send belongs to the step it was
   * made on.
   */
  screen?: StudyStepSlug;
  /** Where this step's half-written message is kept between mounts. */
  draftStore: DraftStore;
  /** The offer standing for this step, or null. */
  restore: ComposerOffer | null;
  /** Hands words back to this step's composer. */
  offer: (
    step: ChatStepSlug,
    text: string,
    onTaken?: () => void,
    options?: { reveal?: boolean; scope?: FollowUpScope; append?: boolean },
  ) => void;
  /** Tells the server to stop a turn; the shell owns what a failed stop says. */
  stopServerTurn: (turnId: string) => void;
  /**
   * A turn has ended while the thread was on screen: one this thread was watching stream, or one on
   * another step that its reads had found running and then found gone.
   */
  onTurnFinished: () => void;
  /** The artifact parts this thread has streamed, for the step's documents. */
  onStreamed: (parts: ArtifactEventPayload[]) => void;
  /**
   * On the Brief, a follow-up finalized: the Brief a choice was answered with, or `null` to read it
   * again. Absent, `onTurnFinished` is told instead.
   */
  onBriefChanged?: (read: BriefReadView | null) => void;
  /** On the Brief, where the composer's unsent questions and card outlive this mount. */
  followUpMemory?: FollowUpMemory;
  /**
   * Whether the Ask Sigora drawer is open, and the way to open or close it. Held by the shell, so
   * the drawer stays open as the researcher walks from one step's thread to the next.
   */
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  /**
   * Told whether a turn is running in this conversation — sent, streaming, or found running and
   * attached to — and told it is not once the thread goes. The Brief's thread does not report.
   */
  onRunningChange?: (running: boolean) => void;
}

const NOTHING_STREAMED: ArtifactEventPayload[] = [];

/**
 * One step's conversation. On the Brief every question is a follow-up, answered by the worker and
 * finished by an outcome in the study record rather than by a turn, so its thread is the follow-up
 * thread; every other step's is a conversation with the agent. The shell keys the thread by step,
 * so which one a mount draws never changes under it.
 */
export const StepThread = (props: StepThreadProps) =>
  props.step === 'brief' ? (
    <FollowUpThread
      ref={props.ref}
      studyId={props.studyId}
      draftStore={props.draftStore}
      restore={props.restore}
      offer={props.offer}
      onFinalized={props.onBriefChanged ?? props.onTurnFinished}
      {...(props.followUpMemory === undefined ? {} : { memory: props.followUpMemory })}
      expanded={props.expanded}
      onExpandedChange={props.onExpandedChange}
    />
  ) : (
    <ConversationThread {...props} />
  );

/**
 * A step's conversation with the agent: its transcript, its sends and the turn it is watching.
 *
 * Keyed by study and step, so each step's thread is its own mount — moving to another step
 * unmounts this one and closes its read of any running turn, while the turn goes on in the worker.
 * Coming back mounts it again, and the transcript read finds the turn still running and attaches
 * to it: navigation, a refresh and a return tomorrow are one path.
 */
const ConversationThread = ({
  ref,
  studyId,
  step,
  screen,
  draftStore,
  restore,
  offer,
  stopServerTurn,
  onTurnFinished,
  onStreamed,
  expanded,
  onExpandedChange,
  onRunningChange,
}: StepThreadProps) => {
  const [activity, setActivity] = useState<string | null>(null);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState<string | null>(null);
  /**
   * The step whose turn the study is running while this thread is on screen, or null.
   *
   * The study runs one turn at a time across all its threads, so a turn on another step is a
   * reason a send from here would be refused, and Send waits while it is set. Set when a read
   * reports such a turn, and when a refusal names another step.
   */
  const [busyStep, setShownBusyStep] = useState<ChatStepSlug | null>(null);
  /**
   * `busyStep` as last set, readable at once.
   *
   * The transcript read compares against it and moves it in one go, so two reads landing before a
   * render cannot both see the same turn as just ended.
   */
  const busyStepNow = useRef<ChatStepSlug | null>(null);
  const setBusyStep = useCallback((next: ChatStepSlug | null) => {
    busyStepNow.current = next;
    setShownBusyStep(next);
  }, []);
  /** The shell's callback, readable from the transcript read without being one of its dependencies. */
  const turnFinished = useRef(onTurnFinished);
  turnFinished.current = onTurnFinished;
  /**
   * Counts the transcript reads that have finished, landed or failed, which is what re-arms the
   * reads that repeat.
   */
  const [threadReads, setThreadReads] = useState(0);
  /**
   * Counts this thread's sends. A read issued before the latest send describes the study from
   * before it, so the turn it found running elsewhere may be one the send has already outlived; it
   * leaves `busyStep` to the send and to whatever the server says about it.
   */
  const sends = useRef(0);
  /**
   * The user messages whose turn the researcher cancelled, so the transcript can say so under the
   * message they sent. View-local and deliberately unpersisted: the row is a real thing the user
   * said and reads back plainly on the next visit, while "cancelled" is about a reply that will
   * not arrive now.
   */
  const [cancelledIds, setCancelledIds] = useState<ReadonlySet<string>>(() => new Set());
  /**
   * The message this view typed to start the running turn, or nothing when nobody typed for it.
   *
   * Cancel is offered on every running turn, and not every running turn was asked for in words: an
   * approval's follow-up is started by the server, and a turn found running on mount was started
   * before this view existed. Reading the transcript's last user message would there name an
   * *earlier*, already-answered message — which would be marked cancelled and dropped back into the
   * composer, both wrong. So the message a cancel acts on is the one recorded when it was sent, and
   * a turn with no record is a turn Cancel stops without marking or restoring anything.
   *
   * `asked` marks a message a step's control sent rather than one typed here, whose words a refusal
   * puts after anything in the composer instead of giving way to it.
   */
  const typedTurn = useRef<{ messageId: string; text: string; asked?: true } | null>(null);
  /**
   * A cancel that arrived before its turn had an id.
   *
   * Cancel is offered from the moment Send is pressed — the composer is disabled by then, so a
   * button that did nothing would be the only thing on screen to press — but the stop it has to
   * make is addressed to a turn the send has not been answered about yet. The intent is held here
   * until the transport says which turn started, and then spent — by the transport, which is why it
   * is spent even when the researcher has left the step in between.
   */
  const stopWanted = useRef(false);
  /**
   * The turn this view asked to stop, and when it asked.
   *
   * Cancelling is a request, and the worker takes a moment to notice it: for a second or two the
   * study still holds the turn and the server refuses a new one. That refusal means something
   * different to someone who has just pressed Cancel than it does to anyone else, and this is what
   * tells the two apart.
   *
   * The turn is named rather than a flag set, because this view starts watching turns it did not
   * send — an approval's follow-up, an answer presentation, a turn found running — and one of those
   * running is a perfectly good reason for a send to be refused that has nothing to do with the
   * cancellation. A stream for any *other* turn is the study having moved on, and closes the window.
   *
   * The time is kept for the one case nothing on screen can answer: a turn started in another tab.
   * Measured on a local stack, the worker lets go of a stopped turn in about two seconds; past the
   * bound below, whatever is refusing a send is not plausibly the turn this view stopped.
   */
  const cancelled = useRef<{ turnId: string | null; at: number } | null>(null);
  /** Words for the send that was refused while the turn it replaces was still stopping. */
  const [sendRefusal, setSendRefusal] = useState<string | null>(null);
  /**
   * Counts the sends refused behind a turn on this thread's own step, and the last of them the
   * transcript has been read again for.
   *
   * Such a turn is one this view is not reading — started from another tab, say — and Send is not
   * held behind it, since its reply belongs on this screen. The read finds it running and attaches
   * to it, so the reply streams here rather than only on the next visit.
   */
  const [ownStepRefusals, setOwnStepRefusals] = useState(0);
  const ownStepRefusalsRead = useRef(0);

  /**
   * Assigned once `useChat` exists, and reached through a ref because the transport is built before
   * it: the transport is what learns the stored id, and `setMessages` is what can act on it.
   */
  const adoptStoredMessageId = useRef<
    ((ids: { clientId: string; messageId: string }) => void) | null
  >(null);

  /**
   * The row the reply now streaming became, reported by the transport as soon as the worker has
   * written it and applied when the stream ends.
   *
   * Held rather than applied on arrival, because the event naming it lands one frame before the
   * turn's terminal one — while the chat is still writing the reply it names. The chat decides
   * whether each write replaces the last message or appends a new one by comparing ids, so
   * renaming underneath it makes the message it is writing into one the list no longer holds, and
   * the next write puts the copy straight back.
   */
  const storedReplyId = useRef<string | null>(null);

  /**
   * Turns this thread has started or attached to, so a read that keeps reporting the same running
   * turn does not open a second read of it.
   */
  const attachedTurns = useRef(new Set<string>());

  /**
   * Whether this thread has been unmounted. A read of a turn that ends because the researcher left
   * the step is not a turn ending anything the shell should act on: the turn is still running.
   */
  const unmounted = useRef(false);

  /**
   * Whether a send is waiting for the server to say which turn it started.
   *
   * Leaving the step then does not close anything: the chat would count a refusal that arrives
   * after the read was closed as part of closing it, and the words the refusal hands back would be
   * lost with it. So the read is left to be opened and is closed the moment the turn has a name.
   */
  const starting = useRef(false);
  /** The chat's own stop, reachable from the transport, which is built before the chat exists. */
  const closeRead = useRef<(() => void) | null>(null);

  const transport = useMemo(() => {
    const http = createHttpTurnStreamSource(studyId, step);

    return createTurnChatTransport(
      {
        ...http,
        start: async (message) => {
          starting.current = true;
          try {
            const started = await http.start(message);

            // The researcher left the step while the send was in flight: the turn runs on, and
            // this thread's read of it closes before it opens.
            if (unmounted.current) {
              closeRead.current?.();
            }

            return started;
          } finally {
            starting.current = false;
          }
        },
      },
      {
        onUserMessageStored: (ids) => adoptStoredMessageId.current?.(ids),
        onAssistantMessageStored: ({ messageId }) => {
          storedReplyId.current = messageId;
        },
        // Anything still held when a new stream begins belongs to a turn that is no longer the one
        // on screen — a cancelled turn whose reply the worker had already written, say — and
        // applying it to this turn's reply would rename it to the previous turn's row, where a
        // later transcript read collapses the pair and the newer reply is the one that goes.
        onStreamStart: (turnId) => {
          storedReplyId.current = null;
          attachedTurns.current.add(turnId);

          // A read of any turn but the stopped one is this study watching something else, which
          // it could only be doing if the server had let the stopped turn go. The cancelled turn's
          // own read is excluded because it is the one a cancel ends — including the read that
          // opens already cancelled, which ends before it has read a frame.
          if (cancelled.current !== null && cancelled.current.turnId !== turnId) {
            cancelled.current = null;
          }
        },
        onTurnStarted: (turnId) => {
          if (stopWanted.current) {
            // This is the turn that was cancelled before it had a name, and its stop is only being
            // asked for now — so the cancellation is beginning here, not ending. The window stays
            // open, which is what the refusal a fast retry meets depends on, and the turn it is
            // about finally has a name to be told apart by.
            stopWanted.current = false;
            cancelled.current = { turnId, at: Date.now() };
            stopServerTurn(turnId);

            return;
          }

          // A turn nobody asked to stop is running, so whatever was being cancelled is behind us.
          cancelled.current = null;
        },
      },
    );
  }, [studyId, step, stopServerTurn]);

  const { messages, setMessages, sendMessage, resumeStream, status, error, stop } =
    useChat<SigoraUIMessage>({
      id: `${studyId}:${step}`,
      transport,
      experimental_throttle: STREAM_PUBLISH_MS,
      /**
       * A send the study refused because a turn is already running.
       *
       * The message the server did not keep is taken back off the screen and the words go back
       * where they can be sent again, whoever is running: this send wrote no row, so what is on
       * screen is a message the conversation does not contain. What is said about it depends on
       * what is running. A turn this view has just cancelled, still stopping, is the one case it
       * understands better than the server does; a turn on another step the refusal names is the notice.
       * Branching on the code rather than the prose, which `errors.ts` is explicit about: the
       * message is the server's to reword.
       *
       * Runs before the chat records the failure, which is what leaves the record of the send
       * still standing to be read here — and runs after this thread has been unmounted, when the
       * researcher left the step with the send in flight, which is why the words go to the shell.
       */
      onError: (thrown) => {
        const refused = typedTurn.current;
        if (refused === null || !isApiError(thrown) || thrown.code !== 'turn_already_running') {
          return;
        }

        // The words go back to the composer, and the copy on screen goes only if they land —
        // declined, the composer already holds something newer and the transcript is where they
        // still are. Words a step's control sent go in after anything typed there since, since the
        // researcher never had them in the composer to keep.
        offer(
          step,
          refused.text,
          () => {
            setMessages((current) => current.filter((message) => message.id !== refused.messageId));
          },
          refused.asked === true ? { reveal: true, append: true } : {},
        );

        const cancellation = cancelled.current;
        if (
          cancellation !== null &&
          Date.now() - cancellation.at < CANCELLATION_EXPLAINS_REFUSAL_FOR_MS
        ) {
          setSendRefusal('Still cancelling the previous turn — try again in a moment.');

          return;
        }

        // A turn on this thread is not running elsewhere, and holding Send behind it would wait
        // on a reply this thread can show; the refusal is shown as it stands, and the transcript is
        // read again to attach to the turn.
        const refusedBy = runningStepOf(thrown);
        if (refusedBy === step) {
          setBusyStep(null);
          setOwnStepRefusals((count) => count + 1);
        } else {
          setBusyStep(refusedBy);
        }
      },
      // Transient parts never reach the transcript, so this callback is the only place they are
      // visible — which is exactly the lifetime a "building your guide…" line should have.
      onData: (part) => {
        if (part.type === 'data-status') {
          setActivity(part.data.message);
        }
      },
      onFinish: ({ message }) => {
        // The reply, renamed to the row it became, now that nothing is still writing into it.
        // This is what stops a transcript read which already returned the reply — a thread opened
        // while a study's answers were being presented into it — leaving the agent apparently
        // saying the same thing twice.
        const stored = storedReplyId.current;
        storedReplyId.current = null;
        if (stored !== null) {
          adoptStoredMessageId.current?.({ clientId: message.id, messageId: stored });
        }

        setActivity(null);

        if (!unmounted.current) {
          onTurnFinished();
        }
      },
    });

  /**
   * What is on screen and how the chat stands, readable from a callback that must not depend on
   * them.
   *
   * The transcript read is a `useCallback` the mount effect runs, so listing `messages` among its
   * dependencies would rebuild it on every publication of a streaming reply and re-read the
   * conversation with it. A ref assigned on each render gives the read the current values without
   * making them dependencies.
   */
  const onScreen = useRef(messages);
  onScreen.current = messages;
  closeRead.current = () => void stop();
  const chatStatus = useRef(status);
  chatStatus.current = status;

  const busy = status === 'submitted' || status === 'streaming';
  useEffect(() => {
    onRunningChange?.(busy);

    return () => onRunningChange?.(false);
  }, [busy, onRunningChange]);

  /**
   * The record lives exactly as long as the turn it describes.
   *
   * Cleared here rather than at each place a turn nobody typed for is adopted — an approval's
   * follow-up, an answer presentation, a turn found running — because every one of those is
   * attached to after the typed turn ahead of it has ended. Clearing on the ending covers them all
   * without their call sites having to know this exists.
   */
  useEffect(() => {
    if (!busy) {
      typedTurn.current = null;
    }
  }, [busy]);

  const streamed = useMemo(() => collectArtifactParts(messages, studyId), [messages, studyId]);

  /**
   * The artifact parts as last handed to the shell, what each one said, and the callback they were
   * handed to; null until the first report, and again once the thread has told the shell it is gone.
   *
   * `streamed` is a new array on every publication of the chat's messages, words included, while
   * the shell, the step and its documents care only about the documents. So the shell is told only
   * when what the parts say changes — a document arriving, moving a version, changing state or
   * content — and never for a reply's words: told per publication, the shell's update from one lands
   * behind the next and a replayed burst reaches React's nested-update limit, which the chat catches
   * from inside its own write and keeps as "Maximum update depth exceeded", ending the read. A new
   * callback has been told nothing yet, so it is handed the parts as they are.
   */
  const lastReported = useRef<{
    parts: readonly ArtifactEventPayload[];
    said: readonly string[];
    to: (parts: ArtifactEventPayload[]) => void;
  } | null>(null);

  useEffect(() => {
    const last = lastReported.current;
    const said = sayChanged(streamed, last);
    if (said !== null) {
      lastReported.current = { parts: streamed, said, to: onStreamed };
    } else if (last !== null && last.to !== onStreamed) {
      lastReported.current = { parts: streamed, said: last.said, to: onStreamed };
    } else {
      return;
    }

    onStreamed(streamed);
  }, [streamed, onStreamed]);

  /**
   * Leaving the step closes this thread's read of whatever it is watching, and nothing more.
   *
   * The chat would otherwise go on reading a turn's stream into a component nobody can see, for
   * the life of the tab. Stopping the read is not stopping the turn — that is a request to the
   * server, which only Cancel makes — so the worker finishes the reply, and the next mount of this
   * step finds it in the transcript or still running. A send still waiting for its turn's id is
   * left to the transport, which closes the read once the id arrives (see `starting`). Set again
   * on mount because a development build mounts every component twice, and depending on nothing
   * the shell hands in, so a callback of the shell's changing identity cannot end a read the
   * researcher is watching.
   */
  const reportTo = useRef({ onStreamed });
  reportTo.current = { onStreamed };

  useEffect(() => {
    unmounted.current = false;

    return () => {
      unmounted.current = true;
      if (!starting.current) {
        void stop();
      }
      lastReported.current = null;
      reportTo.current.onStreamed(NOTHING_STREAMED);
    };
  }, [stop]);

  /**
   * A message on screen, renamed to the row it became.
   *
   * The chat renders a message the moment it is typed, under an id it minted itself, while the
   * database gives the row its own — so a transcript read that lands after the insert returns that
   * row beside the copy already on screen, and the two look like two things a user said once. This
   * is what makes them one: the sent copy takes the stored id, and if the read already brought the
   * row back, the duplicate that creates collapses to the first of the pair.
   *
   * Written to survive either order, because there is no order to rely on: the send's answer and the
   * read's answer are two requests in flight at once. Renaming first leaves a matching id for the
   * merge below to recognise; merging first leaves two copies for this to collapse.
   *
   * Both halves of the conversation come through here. The sent message is renamed the moment the
   * send answers with its row; the reply is renamed when its turn ends, because that is when the
   * worker has written it and nothing is streaming into it any more.
   */
  adoptStoredMessageId.current = ({ clientId, messageId }) => {
    // Both of these name the message by the id it is rendered under, so the rename has to carry
    // them with it or a mark is left pointing at a message that no longer exists and a cancel
    // still to come marks nothing.
    if (typedTurn.current?.messageId === clientId) {
      typedTurn.current = { ...typedTurn.current, messageId };
    }

    setCancelledIds((current) => {
      if (!current.has(clientId)) {
        return current;
      }

      const next = new Set(current);
      next.delete(clientId);
      next.add(messageId);

      return next;
    });

    setMessages((current) =>
      collapseToOneCopy(
        current.map((message) =>
          message.id === clientId ? { ...message, id: messageId } : message,
        ),
      ),
    );
  };

  /**
   * Starts reading a turn this thread did not send, under a line saying why the agent is talking.
   *
   * The line goes in first, and not only to say why. The chat state machine continues the *last*
   * message when that message is the agent's — which, before an approval's follow-up or an answer
   * presentation, it nearly always is — so a resume with nothing in between would stream the new
   * reply into the tail of the old one. A `system` message in between is what makes the reply a
   * message of its own. With `line` absent, it goes in only when the last message is the agent's:
   * a turn found running behind the user's own message needs nothing in front of it.
   */
  const attach = useCallback(
    (turnId: string, line: { id: string; text: string } | null) => {
      attachedTurns.current.add(turnId);
      // A turn is about to be running again, and it is not the one that was being cancelled. The
      // read it starts closes the window too; this says it at the moment the decision is made.
      cancelled.current = null;
      setBusyStep(null);

      if (line !== null) {
        setMessages((current) => [
          ...current,
          { id: line.id, role: 'system', parts: [{ type: 'text', text: line.text }] },
        ]);
      }

      transport.adoptTurn(turnId);
      void resumeStream();
    },
    [transport, resumeStream, setMessages, setBusyStep],
  );

  /**
   * The conversation, read back from the rows the turns wrote, and the turn the study is running.
   *
   * Read on mount, and read again while something is expected to change under it: a finished
   * study's presentation about to start on the Brief step, a turn on another step still running,
   * and a presentation that ended while this thread was on screen.
   *
   * `announceLoading` separates the first read from the rest: it is the reason the chat says it is
   * loading, and a failure of it is worth a sentence. The others happen under a conversation
   * already on screen, so they neither blank anything while they run nor report a banner if they
   * fail.
   *
   * `stillWanted` is checked after the read rather than before, because the thread can be left
   * while it is in flight and acting on it then would attach a thread nobody is looking at.
   */
  const readTranscript = useCallback(
    async ({
      announceLoading,
      stillWanted,
    }: {
      announceLoading: boolean;
      stillWanted: () => boolean;
    }): Promise<void> => {
      if (announceLoading) {
        setHistoryLoading(true);
        setHistoryError(null);
      }

      const sendsAtIssue = sends.current;
      let finished = false;
      try {
        const { messages: history, running } = await fetchMessages(studyId, step);

        if (!stillWanted()) {
          return;
        }

        /**
         * An empty thread opens on Sigora's greeting, so the drawer asks the researcher something
         * rather than opening on nothing.
         *
         * **Only onto an empty screen, and the read is not what makes it empty.** The composer
         * never waits for the transcript, so a researcher who types straight into a new thread
         * has their message on screen — and the reply streaming into it — before this read
         * answers. Appending then would put the greeting under the message it is meant to precede,
         * and the reply would go on being written into the greeting rather than into itself.
         */
        const offerGreeting =
          announceLoading && history.length === 0 && onScreen.current.length === 0;

        /**
         * A turn writing into this thread, found by the read rather than started here: a page
         * opened, refreshed or returned to mid-reply, or a presentation that started on its own.
         * Not while this thread's own turn runs, which the running turn then is.
         */
        const adopted =
          running !== null &&
          running.step === step &&
          !attachedTurns.current.has(running.turnId) &&
          chatStatus.current !== 'submitted' &&
          chatStatus.current !== 'streaming'
            ? running.turnId
            : null;

        /**
         * History goes in front of what is already on screen, which is the whole of the ordering
         * rule: everything in state was said after the read was issued — a message sent while it
         * was in flight. What the two lists have in common is settled by `collapseToOneCopy`,
         * which is also where a message the read has brought back stops being rendered twice.
         */
        setMessages((current) => {
          /**
           * The greeting opens the thread, so it stays first. It lives only on screen, which puts
           * it among what is already there when a later read lands; merged as it stands, the rows
           * the read brings back would go in front of it and the greeting would sit under the
           * exchange it opened — and a line introducing a reply found running would then be
           * written under the greeting rather than under the last thing said.
           */
          const greetingId = `greeting-${studyId}-${step}`;
          const collapsed = collapseToOneCopy([...history, ...current]);
          const shownGreeting = collapsed.find((message) => message.id === greetingId);
          const merged =
            shownGreeting === undefined
              ? collapsed
              : [shownGreeting, ...collapsed.filter((message) => message.id !== greetingId)];
          const opened: SigoraUIMessage[] = offerGreeting
            ? [
                ...merged,
                {
                  id: greetingId,
                  role: 'assistant',
                  parts: [{ type: 'text', text: CHAT_GREETING }],
                },
              ]
            : merged;

          return adopted !== null && opened.at(-1)?.role === 'assistant'
            ? [
                ...opened,
                {
                  id: `attached-${adopted}`,
                  role: 'system',
                  parts: [
                    {
                      type: 'text',
                      text: ALREADY_REPLYING_LINE,
                    },
                  ],
                },
              ]
            : opened;
        });

        /**
         * A turn on another step that was running and no longer is: it ended under a thread that
         * never streamed it, so no `onFinish` here reports it, and what it wrote — the first
         * audience, which the guide's approval drafts on the Guide thread, among others — is read
         * again on this report instead. Only a turn this thread was showing as running elsewhere
         * counts, so a turn it streamed itself is reported once, by `onFinish`.
         */
        const elsewhere =
          running !== null && running.step !== null && running.step !== step ? running.step : null;
        const endedElsewhere = busyStepNow.current !== null && elsewhere === null;

        if (adopted !== null) {
          attach(adopted, null);
        } else if (sendsAtIssue === sends.current) {
          setBusyStep(elsewhere);
        }

        if (endedElsewhere) {
          turnFinished.current();
        }

        finished = true;
      } catch {
        finished = stillWanted();
        if (announceLoading && stillWanted()) {
          setHistoryError(
            'The earlier conversation could not be loaded. You can still send a message.',
          );
        }
      } finally {
        if (announceLoading && stillWanted()) {
          setHistoryLoading(false);
        }
        // A failed read re-arms the repeat as a landed one does: a turn shown as running elsewhere
        // would otherwise hold Send until the researcher leaves the step.
        if (finished) {
          setThreadReads((count) => count + 1);
        }
      }
    },
    [studyId, step, setMessages, attach, setBusyStep],
  );

  /** The read never blocks sending: a chat that refuses input until an old transcript arrives is a
   * worse failure than one missing it. */
  useEffect(() => {
    let active = true;

    void readTranscript({ announceLoading: true, stillWanted: () => active });

    return () => {
      active = false;
    };
  }, [readTranscript]);

  /**
   * A turn running on another step, read again until it is not.
   *
   * The notice is a claim about the study now, and the turn behind it ends on its own; without
   * another look the notice would outlive it and hold Send for nothing. The read that finds the
   * turn gone is also what tells the shell it finished, which is how a document drafted by it
   * reaches the step on screen.
   *
   * A timeout rather than an interval, re-armed by each read finishing, so a slow read is waited
   * for rather than raced and a failed one is tried again — and bounded by the turn itself, since
   * the read that finds it gone ends it.
   */
  useEffect(() => {
    if (busyStep === null || busy) {
      return;
    }

    let active = true;
    const timer = setTimeout(() => {
      void readTranscript({ announceLoading: false, stillWanted: () => active });
    }, BUSY_RECHECK_MS);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [busyStep, busy, threadReads, readTranscript]);

  /**
   * A refusal naming this thread's own step, followed by one read of the transcript once the
   * refused send has settled, which attaches to the turn it reports.
   */
  useEffect(() => {
    if (busy || ownStepRefusals === ownStepRefusalsRead.current) {
      return;
    }

    ownStepRefusalsRead.current = ownStepRefusals;
    let active = true;
    void readTranscript({ announceLoading: false, stillWanted: () => active });

    return () => {
      active = false;
    };
  }, [ownStepRefusals, busy, readTranscript]);

  const send = useCallback(
    (text: string, { asked = false }: { asked?: boolean } = {}) => {
      sends.current += 1;
      setSendRefusal(null);
      setBusyStep(null);
      // A cancel still waiting for a turn id is waiting for the one before this. Unreachable
      // through the composer today — a send whose request is outstanding keeps the chat in
      // `submitted`, where Send is disabled — so this is what keeps that intent from being spent
      // on the message just sent if that ever stops being true.
      stopWanted.current = false;
      // Minted here rather than left to the chat state machine, so that a cancel arriving before
      // the server has answered with a row still knows which message on screen to mark. The SDK's
      // own generator rather than `crypto.randomUUID`, which is not defined outside a secure
      // context — an ip-address origin on a phone is the ordinary way to look at this on a device.
      const messageId = generateId();
      typedTurn.current = { messageId, text, ...(asked ? { asked } : {}) };
      void sendMessage({ id: messageId, parts: [{ type: 'text', text }] });
    },
    [sendMessage, setBusyStep],
  );

  /**
   * Words a step's control asked to send, waiting for this thread to know whether it can: its first
   * transcript read is what says whether a turn is running here or on another step. Held here, so a
   * request goes with the thread when the researcher leaves the step, and is dropped when the step
   * on screen is no longer the one it was made on.
   */
  const [sendRequest, setSendRequest] = useState<{
    text: string;
    token: number;
    screen: StudyStepSlug | undefined;
  } | null>(null);
  /** The composer, which a sent request asks to follow the conversation as a typed send does. */
  const panel = useRef<ChatPanelHandle>(null);
  /** Counts the requests to send, so the latest replaces one not yet decided. */
  const sendRequests = useRef(0);
  /** The last request to send that was decided, so each is decided once. */
  const decidedSendRequest = useRef(0);

  /**
   * A request to send, decided once the first transcript read has landed.
   *
   * Sent as the researcher's own message when nothing is running; the composer's draft is left as
   * it is, and the list follows the message as it does a typed one. Otherwise offered as words,
   * after any already in the composer: a turn running here is not interrupted, and one running
   * elsewhere holds Send behind the notice. Nothing is queued. A turn this thread has just attached
   * to counts as running before the chat says so, since the transport is pointed at it at once and
   * the chat only after its read opens.
   *
   * A request made on a step the researcher has since left, to another step showing this same
   * thread, is dropped undecided: it is sent on neither.
   *
   * Keyed on the request's token, never on the messages: every streamed delta is a new message
   * list, and an effect that ran on each would be a send decided per frame.
   */
  useEffect(() => {
    if (sendRequest === null || decidedSendRequest.current === sendRequest.token) {
      return;
    }
    if (sendRequest.screen !== screen) {
      decidedSendRequest.current = sendRequest.token;

      return;
    }
    if (historyLoading) {
      return;
    }

    decidedSendRequest.current = sendRequest.token;
    if (busy || busyStep !== null || transport.currentTurnId() !== undefined) {
      offer(step, sendRequest.text, undefined, { reveal: true, append: true });

      return;
    }

    panel.current?.follow();
    send(sendRequest.text, { asked: true });
  }, [sendRequest, screen, historyLoading, busy, busyStep, transport, offer, step, send]);

  useImperativeHandle(
    ref,
    () => ({
      adoptApproval: ({ kind, turnId }) => {
        attach(turnId, {
          id: `approval-${turnId}`,
          text: `You approved the ${KIND_LABELS[kind].toLowerCase()}.`,
        });
      },
      ask: (text, { send: sending }) => {
        sendRequests.current += 1;
        if (sending) {
          setSendRequest({ text, token: sendRequests.current, screen });

          return;
        }

        setSendRequest(null);
        offer(step, text, undefined, { reveal: true });
      },
    }),
    [attach, offer, step, screen],
  );

  const label = 'Ask Sigora about this step';

  return (
    <ChatPanel
      collapsible
      expanded={expanded}
      onExpandedChange={onExpandedChange}
      messages={messages}
      status={status}
      activity={activity}
      /*
        This view's own words for a refusal it understands better than the server does, and nothing
        in place of a refusal that names another step, which the notice speaks for while that
        step's turn runs — and only for those. Gated on the error rather than cleared on the way
        out, because what follows a refused send is not always another send: an approval, or a
        presentation arriving on its own, would leave the line standing over a turn streaming
        normally, and a genuine failure of that turn — a provider error, a spend cap — would then be
        shown as a cancellation that had nothing to do with it.
      */
      error={
        isApiError(error) && error.code === 'turn_already_running'
          ? sendRefusal !== null
            ? new Error(sendRefusal)
            : runningStepOf(error) !== null && runningStepOf(error) !== step
              ? undefined
              : error
          : error
      }
      notice={busyStep === null ? null : BUSY_ELSEWHERE_NOTICE}
      sendHeld={busyStep !== null}
      label={label}
      draftStore={draftStore}
      ref={panel}
      restore={restore}
      historyLoading={historyLoading}
      historyError={historyError}
      cancelledIds={cancelledIds}
      onSend={send}
      onStop={() => {
        const turnId = transport.currentTurnId();
        if (turnId === undefined) {
          // Nameless so far: the send has not been answered. The transport reports the id the
          // moment it has one, and the cancel is made then — which is also where the window
          // learns which turn it is about.
          stopWanted.current = true;
        } else {
          stopServerTurn(turnId);
        }

        cancelled.current = { turnId: turnId ?? null, at: Date.now() };

        // Nothing to mark and nothing to give back on a turn nobody typed for.
        const typed = typedTurn.current;
        if (typed !== null) {
          setCancelledIds((current) => new Set(current).add(typed.messageId));
          offer(step, typed.text);
        }

        void stop();
        setActivity(null);
      }}
    />
  );
};
